
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Slab } from "@/types/slab";
import { calculatePricePerSqft } from "@/lib/utils";
import { ProductsHeader } from "@/components/admin/products/ProductsHeader";
import { ProductsFilters } from "@/components/admin/products/ProductsFilters";
import { ProductsTable } from "@/components/admin/products/ProductsTable";
import { SearchBar } from "@/components/catalog/SearchBar";

export default function ProductsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [slabs, setSlabs] = useState<Slab[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>(searchParams.get("type") || "all");
  const [selectedStock, setSelectedStock] = useState<string>(searchParams.get("stock") || "all");
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get("q") || "");
  const [types, setTypes] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ price: number; quantity: number }>({
    price: 0,
    quantity: 0
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      console.log('📊 Iniciando busca de produtos...');
      const { data, error } = await supabase
        .from('cat_slabs')
        .select(`
          *,
          location:cat_locations!cat_slabs_location_id_fkey (
            name
          )
        `);

      if (error) throw error;

      const slabsData = (data || []).map(slab => ({
        id: slab.id,
        code: slab.code,
        name: slab.name,
        price: Number(slab.price),
        pricePerSqft: Number(slab.price_per_sqft),
        sqft: Number(slab.sqft),
        quantity: Number(slab.quantity),
        location: slab.location?.name || '',
        location_id: slab.location_id || '',
        type: slab.type as any,
        imageUrl: slab.image_url || 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b',
        lastUpdated: new Date(slab.last_updated || Date.now()),
        dimensions: slab.dimensions ? {
          length: Number((slab.dimensions as any)?.length || 0),
          height: Number((slab.dimensions as any)?.height || 0)
        } : null,
        barcode: slab.barcode || null,
        shareLink: slab.share_link || null,
        shareLinkExpiry: slab.share_link_expiry ? new Date(slab.share_link_expiry) : null,
      })) as Slab[];

      setSlabs(slabsData);
      
      const uniqueTypes = Array.from(new Set(slabsData.map(slab => slab.type)));
      setTypes(uniqueTypes);

    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast.error('Erro ao carregar produtos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStockFilterChange = (value: string) => {
    setSelectedStock(value);
    searchParams.set("stock", value);
    setSearchParams(searchParams);
  };

  const handleTypeFilterChange = (value: string) => {
    setSelectedType(value);
    searchParams.set("type", value);
    setSearchParams(searchParams);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    searchParams.set("q", value);
    setSearchParams(searchParams);
  };

  const handleStartEdit = (slab: Slab) => {
    setEditingId(slab.id);
    setEditValues({
      price: slab.price,
      quantity: slab.quantity
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({ price: 0, quantity: 0 });
  };

  const handleUpdateField = async () => {
    if (!editingId) return;

    try {
      const slab = slabs.find(s => s.id === editingId);
      if (!slab) return;

      const updates = {
        price: editValues.price,
        quantity: editValues.quantity,
        price_per_sqft: calculatePricePerSqft(editValues.price, slab.sqft)
      };

      const { error } = await supabase
        .from('cat_slabs')
        .update(updates)
        .eq('id', editingId);

      if (error) throw error;

      setSlabs(slabs.map(s => {
        if (s.id === editingId) {
          return {
            ...s,
            price: editValues.price,
            quantity: editValues.quantity,
            pricePerSqft: calculatePricePerSqft(editValues.price, s.sqft)
          };
        }
        return s;
      }));

      toast.success('Produto atualizado com sucesso');
      setEditingId(null);
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      toast.error('Erro ao atualizar produto');
    }
  };

  const filteredSlabs = slabs
    .filter(slab => {
      // Filtro por tipo
      if (selectedType !== "all" && slab.type !== selectedType) return false;
      
      // Filtro por estoque
      switch (selectedStock) {
        case "out":
          return slab.quantity === 0;
        case "low":
          return slab.quantity > 0 && slab.quantity < 2;
        default:
          return true;
      }
    })
    .filter(slab => {
      // Filtro por busca
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      return (
        slab.code.toLowerCase().includes(query) ||
        slab.name.toLowerCase().includes(query) ||
        slab.type.toLowerCase().includes(query)
      );
    });

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="flex-1 ml-[250px]">
        <div className="p-6">
          <ProductsHeader />
          
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <SearchBar 
              value={searchQuery} 
              onChange={handleSearchChange}
            />
            
            <ProductsFilters
              selectedType={selectedType}
              selectedStock={selectedStock}
              types={types}
              onTypeChange={handleTypeFilterChange}
              onStockChange={handleStockFilterChange}
            />
          </div>

          <ProductsTable
            slabs={filteredSlabs}
            isLoading={isLoading}
            editingId={editingId}
            editValues={editValues}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onUpdateField={handleUpdateField}
            onEditValueChange={setEditValues}
          />
        </div>
      </div>
    </div>
  );
}
